import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { 
  CreditCardIcon, 
  PlusCircleIcon, 
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from 'react-hot-toast';

function CardBatchManager() {
  const [cardsData, setCardsData] = useState('');
  const [parsedCards, setParsedCards] = useState([]);
  const [selectedCards, setSelectedCards] = useState(new Set());
  const [format, setFormat] = useState('csv');
  const [isAdding, setIsAdding] = useState(false);
  const queryClient = useQueryClient();

  // Ejemplos con años en AA y AAAA
  const examples = {
    csv: `4111111111111111,12,25,123
5111111111111111,01,2026,456
6011111111111111,05,27,789
371111111111114,12,2025,1234`,
    pipe: `4111111111111111|12|25|123
5111111111111111|01|2026|456
6011111111111111|05|27|789
371111111111114|12|2025|1234`
  };

  // Convertir año a formato AA (2 dígitos)
  const normalizeYear = (year) => {
    const yearStr = year.toString().trim();
    if (yearStr.length === 4) {
      return yearStr.slice(-2);
    }
    if (yearStr.length === 2) {
      return yearStr;
    }
    return yearStr;
  };

  const validateCard = (number, month, year, cvv) => {
    if (!number.match(/^\d{16}$/)) return false;
    if (!month.match(/^(0[1-9]|1[0-2])$/)) return false;
    // Aceptar años de 2 o 4 dígitos
    const yearNum = parseInt(year);
    if (isNaN(yearNum)) return false;
    if (yearNum < 0 || yearNum > 2099) return false;
    if (!cvv.match(/^\d{3,4}$/)) return false;
    return true;
  };

  const detectBrand = (number) => {
    const firstDigit = number[0];
    const firstTwo = number.slice(0, 2);
    const firstFour = number.slice(0, 4);
    
    if (firstDigit === '4') return 'visa';
    if (firstTwo >= '51' && firstTwo <= '55') return 'mastercard';
    if (firstFour === '6011' || firstTwo === '65' || (firstTwo >= '644' && firstTwo <= '649')) return 'discover';
    if (firstTwo === '34' || firstTwo === '37') return 'amex';
    return 'other';
  };

  const parseCards = (text, formatType) => {
    if (!text.trim()) {
      setParsedCards([]);
      return [];
    }

    const lines = text.split('\n').filter(line => line.trim());
    const parsed = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      let parts;
      if (formatType === 'csv') {
        parts = line.split(',').map(p => p.trim());
      } else {
        parts = line.split('|').map(p => p.trim());
      }

      if (parts.length >= 4) {
        const number = parts[0];
        const month = parts[1].padStart(2, '0');
        let year = parts[2];
        const cvv = parts[3];
        
        // Normalizar año (2025 -> 25)
        const normalizedYear = normalizeYear(year);
        
        const isValid = validateCard(number, month, year, cvv);
        
        const card = {
          id: i,
          number,
          expiryMonth: month,
          expiryYear: normalizedYear,
          originalYear: year, // Guardar el año original para mostrar
          cvv,
          isValid
        };
        parsed.push(card);
      }
    }

    setParsedCards(parsed);
    return parsed;
  };

  const handleTextChange = (e) => {
    const text = e.target.value;
    setCardsData(text);
    parseCards(text, format);
    setSelectedCards(new Set());
  };

  const handleFormatChange = (newFormat) => {
    setFormat(newFormat);
    const parsed = parseCards(cardsData, newFormat);
    setParsedCards(parsed);
    setSelectedCards(new Set());
  };

  const toggleSelectCard = (index) => {
    const newSelected = new Set(selectedCards);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedCards(newSelected);
  };

  const selectAll = () => {
    const newSelected = new Set();
    parsedCards.forEach((_, i) => newSelected.add(i));
    setSelectedCards(newSelected);
  };

  const deselectAll = () => {
    setSelectedCards(new Set());
  };

  const addCardsMutation = useMutation(
    async (cardsToAdd) => {
      const promises = cardsToAdd.map(card =>
        api.post('/cards', {
          cardNumber: card.number,
          expiryMonth: card.expiryMonth,
          expiryYear: card.expiryYear,
          cvv: card.cvv,
          cardholderName: `Tarjeta ${card.number.slice(-4)}`,
          brand: detectBrand(card.number),
          cardType: 'credit'
        })
      );
      return Promise.all(promises);
    },
    {
      onSuccess: (results) => {
        queryClient.invalidateQueries('cards');
        queryClient.invalidateQueries('stats');
        const successCount = results.filter(r => r.data?.success !== false).length;
        toast.success(`✅ ${successCount} tarjetas agregadas`);
        
        if (successCount === selectedCards.size) {
          const remaining = parsedCards.filter((_, i) => !selectedCards.has(i));
          if (remaining.length === 0) {
            setCardsData('');
            setParsedCards([]);
          }
        }
        setSelectedCards(new Set());
      },
      onError: () => {
        toast.error('❌ Error al agregar algunas tarjetas');
      }
    }
  );

  const handleAddCards = async () => {
    if (selectedCards.size === 0) {
      toast.error('Selecciona al menos una tarjeta');
      return;
    }

    if (selectedCards.size > 30) {
      toast.error('Máximo 30 tarjetas por lote');
      return;
    }

    const cardsToAdd = parsedCards.filter((_, i) => selectedCards.has(i));
    const invalidCards = cardsToAdd.filter(c => !c.isValid);
    
    if (invalidCards.length > 0) {
      toast.error(`❌ ${invalidCards.length} tarjetas tienen datos inválidos`);
      return;
    }

    setIsAdding(true);
    await addCardsMutation.mutateAsync(cardsToAdd);
    setIsAdding(false);
  };

  const loadExample = () => {
    setCardsData(examples[format]);
    parseCards(examples[format], format);
    setSelectedCards(new Set());
  };

  const clearAll = () => {
    setCardsData('');
    setParsedCards([]);
    setSelectedCards(new Set());
  };

  const copyExample = () => {
    navigator.clipboard.writeText(examples[format]);
    toast.success('📋 Ejemplo copiado al portapapeles');
  };

  const getBrandIcon = (number) => {
    const brand = detectBrand(number);
    const icons = { visa: '💳 VISA', mastercard: '💳 MC', amex: '💳 AMEX', discover: '💳 DSC', other: '💳' };
    return icons[brand];
  };

  return (
    <div style={{ minHeight: '100vh', background: '#000000', padding: '1rem', paddingBottom: '6rem' }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <div style={{ background: '#111111', borderRadius: '1rem', padding: '1.5rem', marginBottom: '1.5rem', border: '1px solid #222222' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
            <CreditCardIcon style={{ width: '2rem', height: '2rem', color: '#00ffff' }} />
            <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffffff' }}>Carga Masiva de Tarjetas</h1>
          </div>
          <p style={{ color: '#9ca3af' }}>Agrega hasta 30 tarjetas de una sola vez. Formato: número,mes,año,cvv</p>
          <p style={{ color: '#ff9900', fontSize: '0.75rem', marginTop: '0.25rem' }}>✓ Años: 25 o 2025 | CVV: 3 o 4 dígitos</p>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <button onClick={() => handleFormatChange('csv')} style={{ padding: '0.5rem 1rem', background: format === 'csv' ? '#00ffff20' : '#1a1a1a', border: format === 'csv' ? '1px solid #00ffff' : '1px solid #333333', borderRadius: '0.5rem', color: format === 'csv' ? '#00ffff' : '#9ca3af', cursor: 'pointer' }}>📊 CSV (coma)</button>
          <button onClick={() => handleFormatChange('pipe')} style={{ padding: '0.5rem 1rem', background: format === 'pipe' ? '#00ffff20' : '#1a1a1a', border: format === 'pipe' ? '1px solid #00ffff' : '1px solid #333333', borderRadius: '0.5rem', color: format === 'pipe' ? '#00ffff' : '#9ca3af', cursor: 'pointer' }}>🔀 Pipe (|)</button>
          <button onClick={loadExample} style={{ padding: '0.5rem 1rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#9ca3af', cursor: 'pointer' }}>📋 Ejemplo</button>
          <button onClick={copyExample} style={{ padding: '0.5rem 1rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#9ca3af', cursor: 'pointer' }}>📄 Copiar</button>
          <button onClick={clearAll} style={{ padding: '0.5rem 1rem', background: '#1a1a1a', border: '1px solid #ff0033', borderRadius: '0.5rem', color: '#ff0033', cursor: 'pointer' }}>🗑️ Limpiar</button>
        </div>

        <div style={{ background: '#1a1a1a', borderRadius: '0.75rem', padding: '0.75rem', marginBottom: '1rem', fontSize: '0.75rem', fontFamily: 'monospace', border: '1px solid #00ffff20' }}>
          <div style={{ color: '#00ffff', marginBottom: '0.25rem' }}>📌 Formato:</div>
          <div style={{ color: '#9ca3af' }}>{format === 'csv' ? 'número,mes,año,cvv' : 'número|mes|año|cvv'}</div>
          <div style={{ color: '#9ca3af', marginTop: '0.25rem' }}>Ejemplo: {format === 'csv' ? '4111111111111111,12,25,123' : '4111111111111111|12|25|123'}</div>
          <div style={{ color: '#ff9900', marginTop: '0.25rem' }}>💡 Años aceptados: 25 (2 dígitos) o 2025 (4 dígitos)</div>
        </div>

        <textarea 
          value={cardsData} 
          onChange={handleTextChange} 
          placeholder={`Pega tus tarjetas aquí\n\nEjemplo:\n4111111111111111,12,25,123\n5111111111111111,01,2026,456\n6011111111111111,05,27,789\n371111111111114,12,2025,1234`} 
          style={{ width: '100%', minHeight: '200px', padding: '1rem', background: '#111111', border: '1px solid #222222', borderRadius: '1rem', color: '#ffffff', fontFamily: 'monospace', fontSize: '0.875rem', resize: 'vertical', marginBottom: '1rem' }} 
        />

        {parsedCards.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={selectAll} style={{ padding: '0.5rem 1rem', background: '#1a1a1a', border: '1px solid #00ffff', borderRadius: '0.5rem', color: '#00ffff', cursor: 'pointer', fontSize: '0.875rem' }}>Seleccionar todas ({parsedCards.length})</button>
              <button onClick={deselectAll} style={{ padding: '0.5rem 1rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.5rem', color: '#9ca3af', cursor: 'pointer', fontSize: '0.875rem' }}>Deseleccionar</button>
            </div>
            <div style={{ color: '#00ffff', fontSize: '0.875rem' }}>{selectedCards.size} / {parsedCards.length} seleccionadas</div>
          </div>
        )}

        {parsedCards.length > 0 && (
          <div style={{ background: '#111111', borderRadius: '1rem', border: '1px solid #222222', overflow: 'hidden', marginBottom: '1.5rem' }}>
            <div style={{ padding: '0.75rem 1rem', background: '#1a1a1a', borderBottom: '1px solid #222222', display: 'grid', gridTemplateColumns: '30px 80px 100px 70px 60px auto 40px', gap: '0.5rem', fontSize: '0.7rem', color: '#9ca3af', fontWeight: '500' }}>
              <div></div><div>Número</div><div>Marca</div><div>Mes/Año</div><div>CVV</div><div>Estado</div><div></div>
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {parsedCards.map((card, index) => (
                <div key={index} onClick={() => toggleSelectCard(index)} style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #222222', display: 'grid', gridTemplateColumns: '30px 80px 100px 70px 60px auto 40px', gap: '0.5rem', alignItems: 'center', background: selectedCards.has(index) ? '#00ffff10' : 'transparent', cursor: 'pointer', fontSize: '0.875rem' }}>
                  <div>{selectedCards.has(index) ? <CheckCircleIcon style={{ width: '1.25rem', height: '1.25rem', color: '#00ffff' }} /> : <div style={{ width: '1.25rem', height: '1.25rem', border: '2px solid #333333', borderRadius: '50%' }} />}</div>
                  <div style={{ fontFamily: 'monospace', color: '#ffffff' }}>•••• {card.number.slice(-4)}</div>
                  <div><span style={{ color: '#9ca3af' }}>{getBrandIcon(card.number)}</span></div>
                  <div style={{ color: '#9ca3af' }}>
                    {card.expiryMonth}/{card.originalYear}
                    <span style={{ fontSize: '0.6rem', marginLeft: '0.25rem', color: '#ff9900' }}>
                      ({card.expiryYear})
                    </span>
                  </div>
                  <div style={{ color: '#9ca3af' }}>***</div>
                  <div>{card.isValid ? <span style={{ color: '#ccff00', fontSize: '0.75rem' }}>✓ Válida</span> : <span style={{ color: '#ff0033', fontSize: '0.75rem' }}>✗ Inválida</span>}</div>
                  <div>{!card.isValid && <XCircleIcon style={{ width: '1rem', height: '1rem', color: '#ff0033' }} />}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {parsedCards.length > 0 && (
          <button onClick={handleAddCards} disabled={isAdding || selectedCards.size === 0} style={{ width: '100%', padding: '1rem', background: 'linear-gradient(135deg, #00ffff, #0066ff)', border: 'none', borderRadius: '1rem', color: '#000000', fontWeight: 'bold', fontSize: '1rem', cursor: isAdding || selectedCards.size === 0 ? 'not-allowed' : 'pointer', opacity: isAdding || selectedCards.size === 0 ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
            {isAdding ? <><ArrowPathIcon style={{ width: '1.25rem', height: '1.25rem', animation: 'spin 1s linear infinite' }} /> Agregando tarjetas...</> : <><PlusCircleIcon style={{ width: '1.25rem', height: '1.25rem' }} /> Agregar {selectedCards.size} tarjeta(s)</>}
          </button>
        )}

        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

export default CardBatchManager;